import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * İki dilli bir katalog alanını (`*Tr` / `*En`) **aktif dile göre** tek bir metne indirger.
 *
 * <p>Yönetim tabloları bu alanları uzun süre **iki ayrı kolon** olarak gösterdi ("Ad (TR)" + "Ad
 * (EN)"); İngilizce çalışan kullanıcı hem gereksiz bir kolon taşıyor hem de bazı ekranlarda (ör.
 * Bildirim Şablonları "Konu (TR)") hiç okuyamadığı bir dili görüyordu. Kullanıcı kararı
 * (24.09.2026): **tablolarda tek kolon**, değeri seçili dilden. <b>Formlar değişmez</b> — orada iki
 * alan da düzenlenebilir olmalıdır.
 *
 * <p>Çağrılabilir olmasının yanında aktif dili de taşır: {@link sortLocalized} sıralamayı doğru
 * alfabeyle yapabilsin diye (dili ayrıca parametre olarak dolaştırmak 20'den fazla kolon dosyasını
 * ikinci bir alanla kirletirdi).
 */
export interface LocalizedText {
  (tr?: string | null, en?: string | null): string
  /** Aktif dil kodu (`tr`, `en`, `en-US` …). */
  readonly language: string
}

/** Dil kodu İngilizce mi? (`en`, `en-US` …) */
function isEnglish(language: string): boolean {
  return language.toLowerCase().startsWith('en')
}

/**
 * Seçili dilin değeri; **o dilde boşsa diğerine düşülür**.
 *
 * <p>Yedeğe düşmek bilinçlidir: katalogların EN adı çoğu tabloda **opsiyoneldir** (yalnız TR
 * zorunludur). Yedek olmasaydı İngilizce arayüzde satırın adı boş görünür, kullanıcı hangi kaydı
 * düzenlediğini anlayamazdı — boş hücre, yanlış dildeki bir addan daha kötüdür.
 */
export function pickLocalized(
  tr: string | null | undefined,
  en: string | null | undefined,
  language: string,
): string {
  const primary = isEnglish(language) ? en : tr
  const fallback = isEnglish(language) ? tr : en
  const chosen = (primary ?? '').trim() !== '' ? primary : fallback
  return chosen ?? ''
}

/**
 * Tabloların **görünen** değere göre sıralaması.
 *
 * <p>Kolona `field` verip `sortable` demek yetmez: `field` ham `*Tr` alanıdır ve İngilizce arayüzde
 * kullanıcı EN adlarını görürken TR adına göre sıralanmış bir liste alırdı — başlık ona yanlış
 * söylerdi. Ayrıca karşılaştırma **dile duyarlıdır** ({@code Intl.Collator}): Türkçede Ç/Ğ/İ/Ö/Ş/Ü
 * kendi yerlerine oturur, ikili (binary) sırada ise listenin sonuna düşerdi.
 *
 * @param pick satırdan `[tr, en]` ikilisini çıkarır
 * @param order PrimeReact'in sıra yönü (1 artan, -1 azalan, yok/0 → dokunma)
 */
export function sortLocalized<T>(
  rows: T[],
  localized: LocalizedText,
  pick: (row: T) => [string | null | undefined, string | null | undefined],
  order: number | null | undefined,
): T[] {
  if (!order) return rows
  const collator = new Intl.Collator(localized.language, { sensitivity: 'base', numeric: true })
  return [...rows].sort((a, b) => {
    const left = localized(...pick(a))
    const right = localized(...pick(b))
    return collator.compare(left, right) * order
  })
}

/**
 * {@link pickLocalized}'ın aktif dile bağlanmış hâli. Dil değiştiğinde `useTranslation` bileşeni
 * yeniden çizer ve yeni işlev üretilir; kolon tanımları da o an yeniden kurulur.
 */
export function useLocalizedText(): LocalizedText {
  const { i18n } = useTranslation()
  const language = i18n.language
  return useMemo(() => {
    const fn = (tr?: string | null, en?: string | null) => pickLocalized(tr, en, language)
    return Object.assign(fn, { language }) as LocalizedText
  }, [language])
}
